import {
  CreateTagType,
  Tag,
  CreateTagInteraction,
  TagCountsResponse,
  TagUpdate,
  VoterTagsType,
  TagInteraction,
} from 'src/@types/tag';
import { useGet, useDelete, usePut } from './templateHooks';
import { useAxios } from './useFetch';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { FAST_API_URL } from 'src/config';
import { useMemo } from 'react';
import { useModelTags } from './modelHooks';

type UseCombinedTagsOptions = Record<string, unknown> & {
  excludeId?: string;
};

export const useTags = (config = {}) =>
  useGet<Tag[]>({
    queryKey: ['tags'],
    endpoint: '/v1/tags',
    config: config,
    url: FAST_API_URL,
  });

export const useCombinedTags = ({ excludeId, ...config }: UseCombinedTagsOptions = {}) => {
  const tagsQuery = useTags(config);
  const modelTagsQuery = useModelTags(config);

  const tags = useMemo(() => tagsQuery.data || [], [tagsQuery.data]);
  const modelTags = useMemo(() => modelTagsQuery.data || [], [modelTagsQuery.data]);
  const data = useMemo(
    () => [...tags, ...modelTags].filter((tag) => tag.id !== excludeId),
    [tags, modelTags, excludeId]
  );

  return {
    data,
    tags,
    modelTags,
    isPending: tagsQuery.isPending || modelTagsQuery.isPending,
    isSuccess: tagsQuery.isSuccess && modelTagsQuery.isSuccess,
    isError: tagsQuery.isError || modelTagsQuery.isError,
  };
};

export const useCreateTag = () => {
  const queryClient = useQueryClient();

  const mutationFn = useAxios<Tag, CreateTagType>({
    endpoint: '/v1/tags',
    method: 'post',
    url: FAST_API_URL,
  });
  return useMutation({
    mutationFn,
    onSuccess: (newTag) => {
      queryClient.setQueryData(['tags'], (oldData: Tag[] | undefined) => [
        ...(oldData || []),
        newTag,
      ]);
      queryClient.invalidateQueries({ queryKey: ['tag-models'] });
      queryClient.invalidateQueries({ queryKey: ['models', 'combined'] });
      queryClient.invalidateQueries({ queryKey: ['filterCategories'] });
      return newTag;
    },
  });
};

export const useCreateModelTag = () => {
  const queryClient = useQueryClient();

  const mutationFn = useAxios<Tag, CreateTagType>({
    endpoint: '/v1/tags',
    method: 'post',
    url: FAST_API_URL,
  });
  return useMutation({
    mutationFn,
    onSuccess: (newTag) => {
      queryClient.setQueryData(['tag-models'], (oldData: Tag[] | undefined) => [
        ...(oldData || []),
        newTag,
      ]);
      queryClient.invalidateQueries({ queryKey: ['tags'] });
      queryClient.invalidateQueries({ queryKey: ['models', 'combined'] });
      queryClient.invalidateQueries({ queryKey: ['filterCategories'] });
      return newTag;
    },
  });
};

export function useUpdateTag() {
  const queryClient = useQueryClient();

  const updateTagMutation = usePut<TagUpdate, Tag>(
    ['tags'],
    '/v1/tags',
    undefined,
    undefined,
    FAST_API_URL
  );

  const updateTag = async (tagId: string, updatedFields: TagUpdate) => {
    await updateTagMutation.mutateAsync({ data: updatedFields, resourceId: tagId });
    queryClient.invalidateQueries({ queryKey: ['tags'] });
    queryClient.invalidateQueries({ queryKey: ['tag-models'] });
    queryClient.invalidateQueries({ queryKey: ['models', 'combined'] });
    queryClient.invalidateQueries({ queryKey: ['filterCategories'] });
  };

  return { updateTag, ...updateTagMutation };
}

export const useCreateTagInteraction = (resourceId: string) => {
  const queryClient = useQueryClient();

  const mutationFn = useAxios<TagInteraction, CreateTagInteraction>({
    endpoint: `/v1/voters/${resourceId}/tags`,
    method: 'post',
    url: FAST_API_URL,
  });
  return useMutation({
    mutationFn,
    onSuccess: (tagInteraction) => {
      queryClient.invalidateQueries({ queryKey: ['voterTagInteractions', resourceId] });
      queryClient.invalidateQueries({ queryKey: ['tagResponses', tagInteraction.tag_id] });
      queryClient.invalidateQueries({ queryKey: ['voterTags', resourceId] });
    },
  });
};

export const useDeleteTags = () =>
  useDelete<{ ids: string[] }, string, Tag>(['tags'], '/v1/tags', FAST_API_URL);

export const useTagCounts = (resourceId: string, config = {}) =>
  useGet<TagCountsResponse>({
    queryKey: ['tagResponses', resourceId],
    endpoint: `/v2/tags/${resourceId}/counts`,
    config: config,
    url: FAST_API_URL,
  });

export const useVoterTags = (voterId: string, config = {}) =>
  useGet<{ [key: string]: { [key: number]: VoterTagsType } }>({
    queryKey: ['voterTags', voterId],
    endpoint: '/v2/voters/' + voterId + '/tags',
    config: config,
    url: FAST_API_URL,
  });
